import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Plus, RefreshCw, ShieldCheck, X } from 'lucide-react';
import AuthContext from '../context/AuthContext';
import { listTransactions } from '../services/transactionService';
import * as chatService from '../services/chatService';
import * as supportService from '../services/supportService';
import EscrowTermsMessage from '../components/chat/EscrowTermsMessage';
import './MessagesPage.css';

const POLL_INTERVAL_MS = 5000;
const POLL_ERROR_MESSAGE = 'Connection interrupted. Retrying…';

function initials(value) {
  return String(value || 'GS').slice(0, 2).toUpperCase();
}

function lastMessageText(chat) {
  const message = chat?.messages?.[chat.messages.length - 1];
  return message?.text || message?.message || message?.body || '';
}

function lastMessageTime(chat, fallback) {
  const message = chat?.messages?.[chat.messages.length - 1];
  return message?.ts || message?.time || message?.sentAt || fallback || '';
}

function formatConversationTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const diffMs = Date.now() - date.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return 'Now';
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function renderMessageContent(text) {
  return String(text || '');
}

export default function Messages() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { txId } = useParams();
  const [transactions, setTransactions] = useState([]);
  const [directChats, setDirectChats] = useState([]);
  const [supportChats, setSupportChats] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loadError, setLoadError] = useState('');
  const [conversationError, setConversationError] = useState('');
  const [readAt, setReadAt] = useState({});
  const [txPresence, setTxPresence] = useState(null);
  const [summoningEscrow, setSummoningEscrow] = useState(false);
  const [escrowHelpStatus, setEscrowHelpStatus] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const loadData = useCallback(async () => {
    setLoadError('');
    const [txResult, directResult, supportResult] = await Promise.allSettled([
      listTransactions(),
      chatService.getDirectChats(),
      supportService.getSupportChats()
    ]);
    if ([txResult, directResult, supportResult].every(result => result.status === 'rejected')) {
      setLoadError('Unable to load conversations right now. Please try again.');
    }

    if (txResult.status === 'fulfilled' && txResult.value.data.success) {
      setTransactions(txResult.value.data.data || []);
    }
    if (directResult.status === 'fulfilled' && directResult.value.data.success) {
      setDirectChats(directResult.value.data.data || []);
    }
    if (supportResult.status === 'fulfilled') {
      setSupportChats(supportResult.value || []);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const conversations = useMemo(() => {
    const txConversations = (transactions || []).map(tx => ({
      id: tx.id,
      type: 'tx',
      title: tx.productTitle || tx.listingTitle || tx.productId || tx.id,
      meta: `${tx.buyerName || tx.buyerId || 'Buyer'} -> ${tx.sellerName || tx.sellerId || 'Seller'}`,
      updatedAt: tx.updatedAt || tx.createdAt || '',
      badge: 'Tx',
      unreadCount: tx.unreadCount || tx.unread || 0,
      online: tx.buyerOnline || tx.sellerOnline || tx.escrowOnline
    }));

    const directConversations = (directChats || []).map(chat => ({
      id: chat.id,
      type: 'direct',
      title: chat.participantName || 'Direct chat',
      meta: chat.lastMessage || lastMessageText(chat) || 'No messages yet',
      updatedAt: chat.updatedAt || lastMessageTime(chat, chat.createdAt),
      badge: 'DM',
      unreadCount: chat.unreadCount || chat.unread || 0,
      online: chat.online || chat.participantOnline
    }));

    const supportConversations = (supportChats || []).map(chat => ({
      id: chat.id,
      type: 'support',
      title: chat.userName ? `Support: ${chat.userName}` : 'Escrow agent',
      meta: lastMessageText(chat) || (chat.closed ? 'Closed support chat' : 'Support conversation'),
      updatedAt: lastMessageTime(chat, chat.createdAt),
      badge: 'Live',
      unreadCount: chat.unreadCount || chat.unread || 0,
      online: !chat.closed
    }));

    return [...txConversations, ...directConversations, ...supportConversations]
      .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
  }, [transactions, directChats, supportChats]);

  const visibleConversations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return conversations;
    return conversations.filter(conversation => (
      `${conversation.title} ${conversation.meta} ${conversation.badge}`
        .toLowerCase()
        .includes(query)
    ));
  }, [conversations, searchQuery]);

  const openTransactionChat = useCallback(async (txId) => {
    setConversationError('');
    setEscrowHelpStatus('');
    try {
      const response = await chatService.getChat(txId);
      setSelectedConv({ id: txId, type: 'tx' });
      setMessages(response.data?.messages || response.data?.chat?.messages || []);
      setReadAt(response.data?.chat?.readAt || {});
      setTxPresence(response.data?.presence || null);
    } catch (e) {
      console.error(e);
      setConversationError('Unable to open this conversation right now.');
      setMessages([]);
      setReadAt({});
      setTxPresence(null);
    }
  }, []);

  const openDirectChat = useCallback(async (chatId) => {
    setConversationError('');
    setEscrowHelpStatus('');
    try {
      const response = await chatService.getDirectChat(chatId);
      const chat = response.data?.chat || response.data?.data;
      setSelectedConv({ id: chatId, type: 'direct' });
      setMessages(chat?.messages || []);
      setReadAt(chat?.readAt || {});
      setTxPresence(null);
    } catch (e) {
      console.error(e);
      setConversationError('Unable to open this conversation right now.');
      setMessages([]);
      setReadAt({});
      setTxPresence(null);
    }
  }, []);

  const openSupportChat = useCallback(async (chatId) => {
    setConversationError('');
    setEscrowHelpStatus('');
    try {
      const chat = chatId === 'new'
        ? await supportService.createSupportChat()
        : await supportService.getSupportChat(chatId);
      if (!chat) {
        setSelectedConv(null);
        setMessages([]);
        setReadAt({});
        setTxPresence(null);
        return;
      }
      setSelectedConv({ id: chat.id, type: 'support' });
      setMessages(chat.messages || []);
      setReadAt(chat.readAt || {});
      setTxPresence(null);
      await loadData();
    } catch (e) {
      console.error(e);
      setConversationError('Unable to open this conversation right now.');
      setMessages([]);
      setReadAt({});
      setTxPresence(null);
    }
  }, [loadData]);

  const openConversation = useCallback((conversation) => {
    if (conversation.type === 'tx') {
      if (txId !== conversation.id) navigate(`/messages/${conversation.id}`);
      return;
    }
    navigate('/messages');
    if (conversation.type === 'support') return openSupportChat(conversation.id);
    if (conversation.type === 'direct') return openDirectChat(conversation.id);
    return openTransactionChat(conversation.id);
  }, [navigate, openDirectChat, openSupportChat, openTransactionChat, txId]);

  useEffect(() => {
    if (!txId || !transactions.some(transaction => transaction.id === txId)) return;
    if (selectedConv?.type !== 'tx' || selectedConv.id !== txId) {
      openTransactionChat(txId);
    }
  }, [openTransactionChat, selectedConv, transactions, txId]);

  useEffect(() => {
    const openTransactionId = location.state?.openTransactionId;
    const openSupportChatId = location.state?.openSupportChatId;
    if (openTransactionId) openTransactionChat(openTransactionId);
    if (openSupportChatId) openSupportChat(openSupportChatId);
  }, [location.state, openSupportChat, openTransactionChat]);

  const selectedConvId = selectedConv?.id;
  const selectedConvType = selectedConv?.type;

  useEffect(() => {
    if (!selectedConvId || !selectedConvType) return undefined;

    const refreshMessages = async () => {
      try {
        if (selectedConvType === 'support') {
          const chat = await supportService.getSupportChat(selectedConvId);
          setMessages(chat?.messages || []);
          setReadAt(chat?.readAt || {});
        } else if (selectedConvType === 'direct') {
          const response = await chatService.getDirectChat(selectedConvId);
          const chat = response.data?.chat || response.data?.data;
          setMessages(chat?.messages || []);
          setReadAt(chat?.readAt || {});
        } else {
          const response = await chatService.getChat(selectedConvId);
          setMessages(response.data?.messages || response.data?.chat?.messages || []);
          setReadAt(response.data?.chat?.readAt || {});
          setTxPresence(response.data?.presence || null);
        }
        setConversationError(current => current === POLL_ERROR_MESSAGE ? '' : current);
      } catch (e) {
        setConversationError(current => current || POLL_ERROR_MESSAGE);
      }
    };

    const interval = setInterval(refreshMessages, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [selectedConvId, selectedConvType]);

  async function sendMessage() {
    if (!draft.trim() || !selectedConv || sendingMessage) return;
    setConversationError('');
    setSendingMessage(true);
    try {
      if (selectedConv.type === 'support') {
        await supportService.postSupportMessage(selectedConv.id, draft.trim());
        await openSupportChat(selectedConv.id);
      } else if (selectedConv.type === 'direct') {
        await chatService.postDirectMessage(selectedConv.id, draft.trim());
        await openDirectChat(selectedConv.id);
      } else {
        await chatService.postMessage(selectedConv.id, draft.trim());
        await openTransactionChat(selectedConv.id);
      }
      setDraft('');
      await loadData();
    } catch (e) {
      console.error(e);
      setConversationError(e.response?.data?.error || 'Unable to send your message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  }

  async function summonEscrow() {
    if (!selectedTransaction || summoningEscrow) return;
    setSummoningEscrow(true);
    setEscrowHelpStatus('');
    setConversationError('');
    try {
      const response = await chatService.summonEscrow(selectedTransaction.id);
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Unable to notify escrow.');
      }
      setMessages(previous => [...previous, response.data.message]);
      setEscrowHelpStatus(response.data.notifiedCount
        ? 'Escrow has been notified to check this conversation.'
        : 'Your request was recorded. No escrow agent is currently available.');
      await loadData();
    } catch (error) {
      setConversationError(error.response?.data?.error || error.message || 'Could not notify escrow. Please try again.');
    } finally {
      setSummoningEscrow(false);
    }
  }

  function senderLabel(message, isMine) {
    const role = message.senderRole || message.role;
    const name = message.senderName;
    if (role === 'admin') return `Admin${name ? ` - ${name}` : ''}`;
    if (role === 'escrow') return `Escrow${name ? ` - ${name}` : ''}`;
    return isMine ? 'You' : (name || 'Customer');
  }

  function closeThread() {
    setSelectedConv(null);
    setMessages([]);
    setReadAt({});
    setTxPresence(null);
    navigate('/messages');
  }

  const selectedConversation = conversations.find(c => c.id === selectedConv?.id && c.type === selectedConv?.type);
  const selectedTransaction = selectedConv?.type === 'tx' ? transactions.find(t => t.id === selectedConv.id) : null;
  const chatTitle = selectedConv
    ? selectedConversation?.title || (selectedConv.type === 'tx' ? `Transaction ${selectedConv.id}` : 'Conversation')
    : 'No conversation selected';
  const chatSubtitle = selectedConv
    ? selectedConv.type === 'support'
      ? 'Customer support conversation'
      : selectedConv.type === 'direct'
        ? 'Direct message'
        : `${selectedTransaction?.buyerName || selectedTransaction?.buyerId || 'Buyer'} -> ${selectedTransaction?.sellerName || selectedTransaction?.sellerId || 'Seller'}`
    : 'Choose a conversation from the left panel';
  const selectedInitials = selectedConv ? initials(chatTitle) : 'GS';
  const userId = user?.id || user?._id;
  const otherPartyId = selectedTransaction
    ? selectedTransaction.buyerId === userId ? selectedTransaction.sellerId : selectedTransaction.buyerId
    : null;
  const canSummonEscrow = selectedTransaction
    && [selectedTransaction.buyerId, selectedTransaction.sellerId].includes(userId)
    && !['completed', 'refunded', 'cancelled'].includes(selectedTransaction.status);

  function getReadReceipts(message) {
    if (!selectedTransaction || message.userId !== userId || !message.ts) return null;
    const hasSeen = participantId => Boolean(participantId && readAt[participantId] && readAt[participantId] >= message.ts);
    const seenByOther = hasSeen(otherPartyId);
    const seenByEscrow = hasSeen(selectedTransaction.escrowId);
    return (
      <span className="gs-read-receipts" aria-label="Message read receipts">
        <span
          className={`gs-read-receipt regular ${seenByOther ? 'seen' : ''}`}
          title="Buyer or seller read receipt"
          aria-label={`Buyer or seller ${seenByOther ? 'has seen' : 'has not seen'} this message`}
        >
          <span aria-hidden="true">✓</span>
          <span aria-hidden="true">✓</span>
        </span>
        {selectedTransaction.escrowId && (
          <span
            className={`gs-read-receipt escrow ${seenByEscrow ? 'seen' : ''}`}
            title="Escrow read receipt"
            aria-label={`Escrow ${seenByEscrow ? 'has seen' : 'has not seen'} this message`}
          >
            <span aria-hidden="true">✓</span>
            <span aria-hidden="true">✓</span>
          </span>
        )}
      </span>
    );
  }

  return (
    <div className="gs-app messages-page">
      <div className="gs-main">
        <div className={`gs-messages-layout ${selectedConv ? 'has-active-thread' : ''}`}>
          <section className="gs-panel gs-conversations">
            <div className="gs-panel-header">
              <div>
                <h2>Messages</h2>
                <p className="gs-conv-preview">Your conversations, orders, and support chats in one place.</p>
              </div>
              <button
                className="gs-circle-btn"
                type="button"
                title="New support chat"
                aria-label="New support chat"
                onClick={() => {
                  navigate('/messages');
                  openSupportChat('new');
                }}
              >
                <Plus size={16} />
              </button>
            </div>

            <div className="gs-conv-search">
              <input
                className="gs-search-input"
                aria-label="Search conversations"
                placeholder="Search chat"
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
              />
            </div>

            <div className="gs-conv-list">
              {loadError && <div className="gs-day-divider" role="alert">{loadError}</div>}
              {visibleConversations.map(conversation => (
                <button
                  key={`${conversation.type}-${conversation.id}`}
                  className={`gs-conv-item ${selectedConv?.id === conversation.id && selectedConv?.type === conversation.type ? 'active' : ''}`}
                  onClick={() => openConversation(conversation)}
                  type="button"
                >
                  <div className="gs-avatar">
                    {initials(conversation.title)}
                    {conversation.online && <span className="gs-online-dot" />}
                  </div>
                  <div className="gs-conv-info">
                    <div className="gs-conv-top">
                      <span className="gs-conv-name">{conversation.title}</span>
                      <span className="gs-conv-time">{formatConversationTime(conversation.updatedAt) || conversation.badge}</span>
                    </div>
                    <div className="gs-conv-bottom">
                      <div className="gs-conv-preview">{conversation.meta}</div>
                      {Number(conversation.unreadCount) > 0 && (
                        <span className="gs-unread-badge">{conversation.unreadCount}</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
              {visibleConversations.length === 0 && (
                <div className="gs-day-divider">No conversations found</div>
              )}
            </div>

            <div className="gs-escrow-chat-wrapper">
              <button
                className={`gs-escrow-chat-btn ${selectedConv?.type === 'support' ? 'active' : ''}`}
                type="button"
                onClick={() => openSupportChat('new')}
              >
                <span className="gs-avatar sm">E</span>
                <div className="gs-escrow-chat-copy">
                  <span>Escrow agent</span>
                  <small>Support chat</small>
                </div>
                <span className="gs-escrow-status">Live</span>
              </button>
            </div>
          </section>

          <section className="gs-panel gs-thread-panel">
            <div className="gs-thread-header">
              <div className="gs-thread-avatar gs-avatar">{selectedInitials}</div>
              <div className="gs-thread-header-info">
                <h3 className="gs-thread-name">{chatTitle}</h3>
                <p className="gs-thread-status">{chatSubtitle}</p>
              </div>
              <div className="gs-thread-header-actions">
                {canSummonEscrow && (
                  <button
                    className="gs-summon-escrow-btn"
                    type="button"
                    onClick={summonEscrow}
                    disabled={summoningEscrow}
                    title="Notify escrow about this transaction"
                  >
                    <ShieldCheck size={15} />
                    {summoningEscrow ? 'Notifying…' : 'Summon escrow'}
                  </button>
                )}
                {selectedTransaction && (
                  <span className="gs-thread-status-badge">
                    {selectedTransaction.status?.replaceAll('_', ' ')}
                    {txPresence?.escrowOnline && <span className="gs-escrow-online-dot" title="Escrow online" />}
                  </span>
                )}
                <button className="gs-circle-btn" type="button" title="Refresh conversations" aria-label="Refresh conversations" onClick={loadData}><RefreshCw size={15} /></button>
                <button
                  className="gs-thread-back"
                  type="button"
                  aria-label="Back to conversations"
                  title="Back to conversations"
                  onClick={closeThread}
                >
                  List
                </button>
                <button className="gs-circle-btn close-chat-btn" type="button" title="Back to marketplace" aria-label="Back to marketplace" onClick={() => navigate('/') }><X size={15} /></button>
              </div>
            </div>

            <div className="gs-thread-body">
              {conversationError && <div className="gs-thread-alert" role="alert">{conversationError}</div>}
              {escrowHelpStatus && <div className="gs-escrow-notice" role="status">{escrowHelpStatus}</div>}
              {!selectedConv && <div className="gs-day-divider">Select a conversation to begin</div>}
              {messages.length === 0 && selectedConv && (
                <div className="gs-day-divider">No messages yet. Send the first message.</div>
              )}

              {messages.map((message, index) => {
                const isMine = message.userId === userId;
                const senderRole = message.senderRole || message.role;
                const isEscrowMessage = senderRole === 'escrow' || senderRole === 'admin' || message.escrow;
                const isEscrowSummon = message.type === 'escrow_summon';
                return (
                  <div key={message.id || index} className={`gs-msg-row ${isMine ? 'mine' : 'theirs'} ${isEscrowMessage ? 'from-escrow' : ''} ${isEscrowSummon ? 'escrow-summon' : ''}`}>
                    <div className={`gs-msg-bubble ${isMine ? 'mine' : 'theirs'} ${isEscrowMessage ? 'from-escrow' : ''} ${isEscrowSummon ? 'escrow-summon' : ''}`}>
                      {(isEscrowMessage || isEscrowSummon) && (
                        <strong className="gs-msg-author">{isEscrowSummon ? 'Escrow requested' : senderLabel(message, isMine)}</strong>
                      )}
                      <div className="gs-msg-text">
                        {message.type === 'escrow_terms'
                          ? <EscrowTermsMessage terms={message.escrowTerms} />
                          : renderMessageContent(message.text || message.message || message.body)}
                      </div>
                      <div className="gs-msg-meta">
                        <span>{new Date(message.ts || message.time || message.sentAt || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        {!isEscrowMessage && !isEscrowSummon && <span>{senderLabel(message, isMine)}</span>}
                        {getReadReceipts(message)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="gs-composer">
              <input
                className="gs-composer-input"
                aria-label="Message"
                placeholder="Write a message..."
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
              />
              <button className="gs-send-btn" type="button" onClick={sendMessage} disabled={!draft.trim() || sendingMessage}>{sendingMessage ? '…' : 'Send'}</button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
